import { PERMISSIONS, RouteItem } from '@typings/Route';
import config from '@utils/config';
import { lazy } from 'react';

export type NavNode = {
    name: string;
    path?: string;
    children?: NavNode[];
    featureFlag?: string;
    hideOnFeatureFlag?: string;
};

const devAuthRoutes: RouteItem[] = [
    {
        name: 'DevAuthLogin',
        path: '/__test/login',
        exact: true,
        component: lazy(() => import('@pages/DevAuth/Login')),
    },
    {
        name: 'DevAuthLogout',
        path: '/__test/logout',
        exact: true,
        component: lazy(() => import('@pages/DevAuth/Logout')),
    },
];

const curriculumRoutes: RouteItem[] = [
    {
        name: 'Curriculum Browser',
        path: '/curriculum',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/Curriculum/Browser')),
        permissions: [PERMISSIONS.CURRICULUM_READ],
    },
    {
        name: 'Curriculum Learning Objective Detail',
        path: '/curriculum/lo/:id',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/Curriculum/LearningObjectiveDetail')),
        permissions: [PERMISSIONS.CURRICULUM_READ],
    },
    {
        name: 'Curriculum Level Plan',
        path: '/curriculum/level/:label',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/Curriculum/LevelPlan')),
        permissions: [PERMISSIONS.CURRICULUM_READ],
    },
    {
        name: 'Curriculum Management',
        path: '/curriculum/manage',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/Curriculum/Manage')),
        permissions: [PERMISSIONS.CURRICULUM_WRITE],
    },
];

const learningPlanRoutes: RouteItem[] = [
    {
        name: 'Unit Themes',
        path: '/learning-plan/units/themes',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/LearningPlan/UnitThemes')),
        permissions: [PERMISSIONS.LEARNING_PLAN_READ],
    },
    {
        name: 'Learning Unit',
        path: '/learning-plan/units',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/LearningPlan/LearningUnits')),
        permissions: [PERMISSIONS.LEARNING_PLAN_READ],
    },
    {
        name: 'Unit Materials',
        path: '/learning-plan/units/:unitId/materials',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/LearningPlan/UnitMaterials')),
        permissions: [PERMISSIONS.LEARNING_PLAN_WRITE],
    },
    {
        name: 'Unit Objectives',
        path: '/learning-plan/units/:unitId/objectives',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/LearningPlan/UnitObjectives')),
        permissions: [PERMISSIONS.LEARNING_PLAN_READ],
    },
    {
        name: 'Material Assignment',
        path: '/learning-plan/materials/assignment',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/LearningPlan/MaterialAssignment')),
        permissions: [PERMISSIONS.LEARNING_PLAN_WRITE],
    },
    {
        name: 'Material Browser',
        path: '/learning-plan/materials/browser',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/LearningPlan/MaterialBrowser')),
        permissions: [PERMISSIONS.LEARNING_PLAN_READ],
    },
];

export const routes: RouteItem[] = [
    {
        name: 'Home',
        path: '/',
        exact: true,
        component: lazy(() => import('@pages/Authentication/Login')),
    },
    // TODO<riez>: Add :hash
    {
        name: 'ClassSeating',
        path: '/class-seating/:hash',
        exact: true,
        component: lazy(() => import('@pages/ClassSeating')),
    },
    {
        name: 'Login',
        path: '/login',
        exact: true,
        component: lazy(() => import('@pages/Authentication/Login')),
    },
    {
        name: 'Forgot Password',
        path: '/forgot-password',
        exact: true,
        component: lazy(() => import('@pages/Authentication/ForgotPassword')),
    },
    {
        name: 'Reset Password',
        path: '/reset-password',
        exact: true,
        component: lazy(() => import('@pages/Authentication/ResetPassword')),
    },
    {
        name: 'Schedule',
        path: '/schedule',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/Schedule')),
        hideOnFeatureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Schedule',
        path: '/schedule/v2',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/ScheduleV2')),
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Participation Tracker',
        path: '/participation-tracker/:sessionId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/ParticipationTracker')),
    },
    {
        name: 'Teacher Feedback',
        path: '/feedback',
        exact: true,
        showOnNavbar: true,
        component: lazy(() => import('@pages/TeacherFeedbackList')),
        additionalComponents: ['notification-bubble'],
        permissions: [PERMISSIONS.FEEDBACK_READ],
        hideOnFeatureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Teacher Feedback Form',
        path: '/feedback/:sessionId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/TeacherFeedback/TeacherFeedbackV2')),
        permissions: [PERMISSIONS.FEEDBACK_WRITE],
    },
    {
        name: 'Teacher Feedback V3',
        path: '/teacher-feedback/v3/:sessionId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/TeacherFeedback/TeacherFeedbackV3')),
        permissions: [PERMISSIONS.FEEDBACK_WRITE],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Teacher Feedback Math',
        path: '/teacher-feedback/math/:sessionId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/TeacherFeedback/TeacherFeedbackMath')),
        permissions: [PERMISSIONS.FEEDBACK_WRITE],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Teacher Feedback Math Worksheet Assignment',
        path: '/teacher-feedback/math/:sessionId/students/:studentId/worksheet-assignment',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/TeacherFeedback/WorksheetAssignmentMath')
        ),
        permissions: [PERMISSIONS.FEEDBACK_WRITE],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Student Tracker',
        path: '/student-level-tracker',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/LevelTracker/LevelTrackerPage')),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Book Progress',
        path: '/math/book-progress',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/MathBookProgress/MathBookProgressRedirectPage')
        ),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Math Book Progress',
        path: '/math/book-progress/student/:studentId',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/MathBookProgress/StudentProgressBookPage')
        ),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Math Learning Summary List',
        path: '/math/learning-summary',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/MathBookProgress/MathLearningSummaryListPage')
        ),
        permissions: [PERMISSIONS.LEARNING_SUMMARY_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Math Learning Summary Detail',
        path: '/math/learning-summary/:id',
        exact: true,
        showOnNavbar: false,
        component: lazy(() =>
            import('@pages/MathBookProgress/StudentProgressBookPage').then((m) => ({
                default: m.MathLearningSummaryDetailPage,
            }))
        ),
        permissions: [PERMISSIONS.LEARNING_SUMMARY_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Math Student Tracker',
        path: '/math/student-tracker',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/MathStudentTracker/MathStudentTrackerPage')
        ),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Learning Summary',
        path: '/learning-summary',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/LearningSummary/LearningSummaryPage')),
        permissions: [PERMISSIONS.LEARNING_SUMMARY_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Learning Summary Detail',
        path: '/learning-summary/:id',
        exact: true,
        showOnNavbar: false,
        component: lazy(
            () => import('@pages/LearningSummary/LearningSummaryDetailPage')
        ),
        permissions: [PERMISSIONS.LEARNING_SUMMARY_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Student Level Tracker Details',
        path: '/student-level-tracker/:studentId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/LevelTracker/StudentDashboardPage')),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Unit Tracker',
        path: '/unit-tracker/:unitId',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/Tracker/UnitTracker')),
        permissions: [PERMISSIONS.TRACKER_READ],
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Authentication Verify',
        path: '/authentication/verify',
        exact: true,
        showOnNavbar: false,
        component: lazy(() => import('@pages/Authentication/AuthenticationVerify')),
    },
    ...curriculumRoutes,
    ...learningPlanRoutes,
    ...(config.environment === 'production' ? [] : devAuthRoutes),
];

export const navTree: NavNode[] = [
    {
        name: 'Schedule',
        path: '/schedule/v2',
        featureFlag: 'ffPaulaNewCurriculum',
    },
    {
        name: 'Curriculum',
        children: [
            { name: 'Browser', path: '/curriculum' },
            { name: 'Management', path: '/curriculum/manage' },
        ],
    },
    {
        name: 'Teacher Feedback',
        hideOnFeatureFlag: 'ffPaulaNewCurriculum',
        children: [{ name: 'Teacher Feedback Sessions', path: '/feedback' }],
    },
    {
        name: 'Learning Plan',
        children: [
            {
                name: 'Learning Unit',
                children: [
                    { name: 'Browser', path: '/learning-plan/units' },
                    { name: 'Themes', path: '/learning-plan/units/themes' },
                ],
            },
            {
                name: 'Learning Material',
                children: [
                    { name: 'Browser', path: '/learning-plan/materials/browser' },
                    {
                        name: 'Assignment',
                        path: '/learning-plan/materials/assignment',
                    },
                ],
            },
        ],
    },
    {
        name: 'Student Tracker',
        featureFlag: 'ffPaulaNewCurriculum',
        children: [
            { name: 'English', path: '/student-level-tracker' },
            { name: 'Math', path: '/math/student-tracker' },
        ],
    },
    {
        name: 'Learning Summary',
        featureFlag: 'ffPaulaNewCurriculum',
        children: [
            { name: 'English', path: '/learning-summary' },
            { name: 'Math', path: '/math/learning-summary' },
        ],
    },
    {
        name: 'Schedule',
        path: '/schedule',
        hideOnFeatureFlag: 'ffPaulaNewCurriculum',
    },
];
